/**
 * Борлуулалтын блокийн шүүлтүүрийн мөр — хугацаа ба салбар.
 * Доорх гол үзүүлэлт, салбарын жагсаалт хоёулаа ИЖИЛ шүүлтүүрээр.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ReportPreset } from '@boots/domain';
import { api, download, type Envelope } from '../../lib/api';
import type { Store } from '../../lib/types';
import { salesQuery, type ReportFilter } from '../../lib/use-report-filter';
import { Button, Tabs } from '../ui';

const PRESETS: { key: ReportPreset; label: string }[] = [
  { key: 'today', label: 'Өнөөдөр' },
  { key: 'yesterday', label: 'Өчигдөр' },
  { key: 'month', label: 'Энэ сар' },
  { key: 'prev-month', label: 'Өмнөх сар' },
  { key: 'year', label: 'Энэ он' },
  { key: 'custom', label: 'Сонгох' },
];

const FIELD =
  'min-h-11 rounded-full border border-hair bg-panel px-3.5 py-1 text-base text-ink ' +
  'focus:border-accent focus:outline-none sm:min-h-0 sm:text-xs';

export function SalesFilters({ f, showExport = true }: {
  f: ReportFilter;
  /**
   * CSV товч. Тайлангийн хуудсанд байна; захиалгын хуудсанд ҮГҮЙ —
   * тэнд `reports.view` эрхгүй хүн ч ордог тул товч нь 403 өгнө.
   */
  showExport?: boolean;
}) {
  // Барааны хуудастай ижил түлхүүр — лавлах өгөгдөл, дахин татахгүй
  const { data: stores } = useQuery({
    queryKey: ['ref-stores'],
    queryFn: () => api<Envelope<Store[]>>('/ref/stores'),
    staleTime: 5 * 60_000,
  });

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Tabs tabs={PRESETS} value={f.preset} onChange={(preset) => f.set({ preset })} />

      {f.preset === 'custom' ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            type="date"
            aria-label="Эхлэх огноо"
            value={f.from}
            max={f.to || undefined}
            onChange={(e) => f.set({ from: e.target.value })}
            className={FIELD}
          />
          <span aria-hidden className="text-ink-3">–</span>
          <input
            type="date"
            aria-label="Дуусах огноо"
            value={f.to}
            min={f.from || undefined}
            onChange={(e) => f.set({ to: e.target.value })}
            className={FIELD}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
        <select
          aria-label="Салбар"
          value={f.storeId}
          onChange={(e) => f.set({ storeId: e.target.value })}
          className={`${FIELD} max-w-[200px] ${
            f.storeId ? 'border-accent-700 bg-accent-100 text-accent-700' : 'text-ink-2'
          }`}
        >
          <option value="">Бүх салбар</option>
          {(stores?.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}{s.type === 'WAREHOUSE' ? ' · агуулах' : ''}
            </option>
          ))}
        </select>
        {showExport ? <ExportButton f={f} /> : null}
      </div>
    </div>
  );
}

/**
 * CSV — одоогийн шүүлтээр, зарсан гутал бүр нэг мөр.
 * Энэ хуудас `reports.view` шаарддаг тул товч харагдсан хүн бүр экспортлох эрхтэй.
 */
function ExportButton({ f }: { f: ReportFilter }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const r = f.range;

  const run = async () => {
    if (!r) return;
    setBusy(true);
    setError(null);
    try {
      await download(`/reports/sales/export?${salesQuery(r.from, r.to, f.storeId)}`, 'sales.csv');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="ghost" onClick={() => void run()} disabled={!r || busy} className="sm:py-1 sm:text-xs">
        {busy ? 'Бэлдэж байна…' : 'CSV татах'}
      </Button>
      {error ? <span role="alert" className="text-meta text-alert-ink">{error}</span> : null}
    </>
  );
}
